import type { ReactNode } from "react";
import { ThumbsDownIcon, ThumbsUpIcon } from "lucide-react";
import { cn } from "../lib/utils";
import type { ChatFeedbackValue, ChatRating } from "./v2/ChatMessage";

/** A rating of an answer and, optionally, why. */
export interface Rated {
  rating: ChatRating;
  comment?: string | null;
}

export interface Review {
  id: string;
  /** A stored review: 1 or -1, as the server sends it. */
  rating: number;
  comment?: string | null;
  /** Null for a reviewer from Lytra: the tenant cannot read their name. */
  reviewerName?: string | null;
  isOwn: boolean;
}

/** A stored rating as the chat's thumbs take it: 1, -1, or null for none. */
export function chatRating(rating: number | null | undefined): ChatFeedbackValue {
  return rating === 1 || rating === -1 ? rating : null;
}

/** A stored rating and its comment; null when the answer has no rating. */
export function rated(rating: number | null | undefined, comment?: string | null): Rated | null {
  const value = chatRating(rating);
  return value === null ? null : { rating: value, comment };
}

/** One line: the rating as a coloured icon, who gave it, and what they said. */
function ReviewLine({ rating, name, children }: { rating: number; name: string; children?: ReactNode }) {
  const Icon = rating === 1 ? ThumbsUpIcon : ThumbsDownIcon;
  return (
    <li className="flex items-start gap-1.5 text-muted-foreground">
      <Icon
        className={cn(
          "mt-0.5 h-3 w-3 shrink-0",
          rating === 1 ? "text-success-strong" : "text-destructive",
        )}
        strokeWidth={1.75}
      />
      <span className="min-w-0">
        <span className="font-medium text-foreground">{name}</span>
        {children}
      </span>
    </li>
  );
}

/** What others said of one chat answer, as small read-only lines: the customer's own
 * rating and the other reviewers' reviews. The reader rates through the answer's thumbs. */
export function AnswerReviews({
  reviews,
  customerFeedback = null,
}: {
  /** Other reviewers' reviews; the reader's own shows on the answer's thumbs. */
  reviews: Review[];
  /** What the customer said of the answer in the chat. */
  customerFeedback?: Rated | null;
}) {
  if (customerFeedback == null && reviews.length === 0) return null;
  return (
    <ul className="flex flex-col gap-1 text-xs">
      {customerFeedback != null && (
        <ReviewLine rating={customerFeedback.rating} name="Kundenbewertung">
          {": "}
          <span>{customerFeedback.rating === 1 ? "hilfreich" : "nicht hilfreich"}</span>
          {customerFeedback.comment && (
            <>
              {". "}
              <span className="whitespace-pre-wrap">{customerFeedback.comment}</span>
            </>
          )}
        </ReviewLine>
      )}
      {reviews.map((review) => (
        <ReviewLine key={review.id} rating={review.rating} name={review.reviewerName ?? "Lytra"}>
          {review.comment && (
            <>
              {": "}
              <span className="whitespace-pre-wrap">{review.comment}</span>
            </>
          )}
        </ReviewLine>
      ))}
    </ul>
  );
}
